import {
  deleteRequest,
  getRequest,
  postFormRequest,
  postRequest,
  putFormRequest,
  putRequest,
} from 'apis/fetcher'
import {
  AdminHireCandidatePutRequestParams,
  AlertSettingsPayload,
  ApplicationsAcceptPayload,
  DashboardChatStatusId,
  DashboardPositionParams,
  DashboardScoutRequestOneQueryParams,
  DashboardScoutRequestSearchFilterParams,
  DashboardTempScoutProposal,
  DesireMeetParams,
  Envelope,
  FileSystemVersion,
  NumberOrNull,
  PrivateInfoDetectedResponse,
  ScoutProfileDraftType,
  ScoutRequestParams,
  ApplicationParams,
  ScoutSearchSessionEventType,
  AdminBrokingRequestMeetingInfo,
  StartupPositionCurationFeedbackType,
  InterestType,
  DashboardPositionAdvertisementsParams,
  AdminPerStartupCurationCreateParams,
  AdminPerStartupCurationUpdateParams,
  BoostingWithItem,
  DashboardScoutRequestDetail,
  ScoutSearchCandidate,
  DashboardScoutProposalMessageCreationMode,
  DashboardAutoPosition,
  ScoutFulfillment,
  AccountAlertSetting,
  ScoutProposalSelectAnswerEnum,
  StartupPositionAnalysis,
  BrokingRequestHiringProcessType,
} from 'types/common'
import qs from 'query-string'
import { UserAccount } from 'types/common'
import { analytics } from 'infra/analytics'
import dayjs from 'dayjs'

export const postAccountsSwitchUser = (params?: { tempToken?: string }) =>
  postRequest('/accounts/switch-user', params)

export const postRequestAdminHireConfirm = (id: number) =>
  postRequest(`/admin/hires/${id}/confirm`)

export const postRequestAdminManualMatches = (params: {
  userId: number
  startupId: number
}) => postRequest(`/admin/manual-matches`, params)

export const postRequestScoutProposalAllowResume = (
  proposalId: number,
  params: { isResumeAllowed: boolean },
) => postRequest(`/scouts/proposals/${proposalId}/allow-resume`, params)

export const postRequestScoutProposalView = (id: number) =>
  postRequest(`/scouts/proposals/${id}/view`)

export const postRequestDashboardScoutProposalProfileFillUp = (id: number) =>
  postRequest(`/dashboard/scouts/proposals/${id}/request-profile-fill-up`)

export const postRequestDashboardScoutProposalHire = (
  params: { proposalId: number; workStartDate: string | null }[],
) => postRequest(`/dashboard/scouts/proposals/hire`, params)

export const postRequestDashboardScoutProposalCancel = (
  id: number,
  params: { cancelMessage: string },
) => postRequest(`/dashboard/scouts/proposals/${id}/cancel`, params)

export const postRequestScoutProposalReject = (
  id: number,
  params: { rejectMessage: string },
) => postRequest(`/scouts/proposals/${id}/reject`, params)

export const postRequestDashboardApplicationAccept = (
  params: ApplicationsAcceptPayload[],
) => postRequest(`/dashboard/applications/accept`, params)

export const postRequestDashboardApplicationReject = (
  id: number,
  params: {
    rejectMessage: string | null
    scheduledRejectionDate: string | null
    sendNotification?: boolean
  },
) => postRequest(`/dashboard/applications/${id}/reject`, params)

export const postRequestDashboardApplicationScheduledRejectionCancel = (
  applicationId: number,
) =>
  postRequest(
    `/dashboard/applications/${applicationId}/scheduled-rejections/cancel`,
  )

export const postRequestDashboardAccountMe = (params: {
  agreedHireNotification?: boolean
  agreedPenaltyForHireAfterRejection?: boolean
  agreedPenaltyWhenIntentionalHiding?: boolean
  agreedPenaltyForHireAfterCancellation?: boolean
}) => postRequest('/dashboard/accounts/me', params)

export const putDashboardAccountMe = (params: {
  password?: string
  shouldAutoRemindProposalPendingTalent?: boolean
  isAutoProposalGenerationEnabled?: boolean
}) => putRequest('/dashboard/accounts/me', params)

export const postRequestDashboardScoutInterestedInMeView = () =>
  postRequest('/dashboard/scouts/requests/interested-in-me/view')

export const postRequestDashboardScoutCurationRequestUninterestedFeedback = (
  curationId: number,
  requestId: number,
  params: { value: string },
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/uninterest-feedback`,
    params,
  )

export const postRequestDashboardScoutTalentRecommendRequest = (params: {
  message?: string
  positionId?: number
}) => postRequest('/dashboard/scouts/talent-recommend-request', params)

export const postRequestDashboardScoutRequestView = (id: number) =>
  postRequest(`/dashboard/scouts/requests/${id}/view`)

export const postRequestDashboardScoutSaveList = (params: { name: string }) =>
  postRequest('/dashboard/scouts/save-list', params)

export const postRequestDashboardStartupRequestInfoFill = (params: {
  type: string
  link: string | null
}) => postRequest(`/dashboard/startup/request-info-fill`, params)

export const postRequestAccountMeStatus = (params: {
  isActive: boolean
  deactivateReason?: string
}) => postRequest('/accounts/me/status', params)

export const postRequestDashboardChatConfirmMeet = (params: {
  proposalId: NumberOrNull
  applicationId: NumberOrNull
  date: string
  isOnline: boolean
  isOffline: boolean
  onlineLink: string
  offlineAddress: string
  meetDesireId?: number
}) => postRequest(`/dashboard/chats/confirm-meet`, params)

export const postRequestScoutProposalDesireMeet = (
  id: number,
  params: DesireMeetParams,
) => postRequest(`/scouts/proposals/${id}/desire-meet`, params)

export const postRequestDashboardScoutProposalProposeMeet = (
  id: number,
  params: { isAddress: boolean },
) => postRequest(`/dashboard/scouts/proposals/${id}/propose-meet`, params)

interface VerificationParams {
  name: string
  phone: string
  birth: string
}

export const postRequestAccountMeVerifyCareerHistory = (
  params: VerificationParams,
) => postRequest('/accounts/me/verifications/careers', params)

export const postRequestAccountMeVerifyCareerAndSalaryHistory = (
  params: VerificationParams,
) => postRequest('/accounts/me/verifications/careers/salary', params)

export const postRequestBlockedCompanies = (params: {
  startupIds: number[]
  companyIds: number[]
}) => postRequest('/scouts/requests/me/blocked-companies', params)

export const deleteRequestBlockedCompanies = (params: {
  startupIds: number[]
  companyIds: number[]
}) => deleteRequest('/scouts/requests/me/blocked-companies', params)

export const putRequestScoutRequestMe = (params: Partial<ScoutRequestParams>) =>
  putRequest('/scouts/requests/me', params).then(
    (res) => res as Envelope<PrivateInfoDetectedResponse>,
  )

export const postRequestDashboardScoutCurationRequestUninterested = (
  curationId: number,
  requestId: number,
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/uninterest`,
  )

export interface BrokingRequestParams {
  hasRequestedMeetingInstead: boolean
  brokingRequestReason: string
  hiringProcessType: BrokingRequestHiringProcessType
  otherDescription: string
  meetLocation: string
  contact: string
}

export const postRequestDashboardScoutCurationRequestBroking = (
  curationId: number,
  requestId: number,
  params: BrokingRequestParams,
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/request-broking`,
    params,
  )

export const postRequestScoutProposalAccept = (id: number) =>
  postRequest(`/scouts/proposals/${id}/accept`)

export const postRequestChatDismiss = (id: number) =>
  postRequest(`/chat/dismiss?proposalId=${id}`)

export const postRequestScoutRequestMeInfoFill = (params: {
  mainFile: number | null
  files: number[]
  links: number[]
}) => postRequest('/scouts/requests/me/request-info-fill', params)

export const postRequestScoutRequestMeSendBackLink = () =>
  postRequest('/scouts/requests/me/send-back-link')

export const postRequestDashboardScoutProposalRequestResume = (id: number) =>
  postRequest(`/dashboard/scouts/proposals/${id}/request-resume`)

export const postRequestDashboardAcknowledgeHiring = () =>
  postRequest('/dashboard/acknowledge-hiring')

export const postRequestDashboardAccountMeStartupPic = (params: {
  name: string
  position: string
  phone: string
  email: string
}) => postRequest('/dashboard/accounts/me/startup-pic', params)

export const postRequestDashboardAccountMeStartupPicAlert = (
  id: number,
  params: AlertSettingsPayload[],
) => postRequest(`/dashboard/accounts/me/startup-pic/${id}`, params)

export const postRequestAdminAutoLoginLink = (params: {
  startupId: number
  path: string
}) => postRequest<{ link: string }>(`/admin/auto-login-link`, params)

export const postRequestDashboardAccountFindPassword = (params: {
  username: string
}) =>
  postRequest<{ email: string }>('/dashboard/accounts/find-password', params)

export const postRequestDashboardSaveAccessLog = (params: {
  hireStatus: string
  alertLevel: string
}) => postRequest(`/dashboard/save-access-log?${qs.stringify(params)}`)

export const postRequestDashboardScoutCurationView = (
  id: number,
  params?: { isAcceptanceTab: boolean },
) => postRequest(`/dashboard/scouts/curations/${id}/view`, params)

export const postRequestAccountMeApplicationReportProcessNotNoted = (
  id: number,
) => postRequest(`/accounts/me/applications/${id}/reportProcessNotNoted`)

export const postRequestStartupInterestedInMeView = () =>
  postRequest('/startups/interested-in-me/view')

interface postRequestTestUserParams
  extends Omit<UserAccount, 'positions' | 'techStacks' | 'experience'> {
  experience?: number
  interestedPositionTypeIds?: number[]
  interestedTechStackIds?: number[]
}

export const postRequestTestUser = (params: postRequestTestUserParams) =>
  postRequest('/test/users', params)

export const postAdminPerStartupCurationScoutRequest = (
  curationId: number,
  params: { scoutRequestIds: number[] },
) =>
  postRequest(
    `/admin/per-startup-curations/${curationId}/scout-requests`,
    params,
  )

export const postAdminPerStartupCuration = (
  params: AdminPerStartupCurationCreateParams,
) => postRequest(`/admin/per-startup-curations`, params)

export const postRequestStartupPositionApplicationTempApply = (id: number) =>
  postRequest(`/startup-positions/${id}/applications/temp/apply`)

export const postRequestDashboardScoutProposalGenerate = (
  id: number,
  params: { fields: string[]; refresh: boolean },
) =>
  postRequest<{ proposeReason: string; appealPoints: string }>(
    `/dashboard/scouts/requests/${id}/proposal/generate`,
    params,
  )

export const postRequestDashboardApplicationExtend = (id: number) =>
  postRequest(`/dashboard/applications/${id}/extend`)

export const postRequestDashboardApplicationAcceptResume = (
  id: number,
  params: { message: string },
) => postRequest(`/dashboard/applications/${id}/accept-resume`, params)

export const postRequestDashboardApplicationView = (id: number) =>
  postRequest(`/dashboard/applications/${id}/view`)

export const postRequestDashboardAuthCode = (params: { email: string }) =>
  postRequest(`/dashboard/auth-code`, params)

export const postRequestDashboardLoginByEmail = (params: {
  email: string
  authCode: string
}) => postRequest(`/dashboard/login-by-email`, params)

export const postRequestDashboardLogin = (params: {
  username: string
  password: string
}) => postRequest(`/dashboard/login`, params)

export const postRequestDashboardPositions = (
  params: DashboardPositionParams,
) => postRequest<PrivateInfoDetectedResponse>(`/dashboard/positions`, params)

export const putRequestDashboardPositions = (
  positionId: number,
  params: DashboardPositionParams,
) =>
  putRequest<PrivateInfoDetectedResponse>(
    `/dashboard/positions/${positionId}`,
    params,
  )

export const putRequestDashboardStartup = (params: FormData) =>
  putFormRequest<PrivateInfoDetectedResponse>(`/dashboard/startup`, params)

export const postRequestDashboardScoutSaveListRequest = (
  id: number,
  params: { scoutRequestId: number },
) => postRequest(`/dashboard/scouts/save-list/${id}/requests`, params)

export const putRequestDashboardScoutRequestProposal = (
  proposalId: number,
  shouldDetect: boolean,
  params: DashboardTempScoutProposal,
) =>
  putRequest<PrivateInfoDetectedResponse>(
    `/dashboard/scouts/requests/${proposalId}/proposal?shouldDetect=${shouldDetect}`,
    params,
  )

export const postRequestDashboardScoutRequestProposalFinalize = (id: number) =>
  postRequest<{ proposalId: number }>(
    `/dashboard/scouts/requests/${id}/proposal/finalize`,
  )

export const putRequestDashboardScoutProposalSettings = (params: {
  messageCreationMode?: DashboardScoutProposalMessageCreationMode
  isRelatedStartupPositionAutoSelectionEnabled?: boolean
  isMatchingRequirementAutoSelectionEnabled?: boolean
}) => putRequest(`/dashboard/scouts/proposals/settings`, params)

export const postRequestAccountMeStart = (params: {
  name: string
  phone: string
  email: string
  experience: number
  acquisitionFunnel: string | null
  recommenderPhone: string | null
  firstLandingPath: string | null
  interestedPositionTypes: number[]
  interestedTechStacks: number[]
  tempToken: string
}) => postRequest('/accounts/me/start', params)

export const postRequestStartupPositionView = (id: number) =>
  postRequest(`/startup-positions/${id}/view`)

export const postRequestStartupView = (id: number) =>
  postRequest(`/startups/${id}/view`)

export const postRequestAnalyticsStartupScoutSearchSessionEvents = (params: {
  type: ScoutSearchSessionEventType
  data?: string
}) => postRequest('/analytics/startups/scout-search-sessions/events', params)

export const postAdminHireTrackingStartupMemo = (
  startupId: number,
  params: { memo: string },
) => postRequest(`/admin/hires/tracking/startups/${startupId}`, params)

export const putAdminHireTrackingStartupInfo = (
  startupId: number,
  params: { isHide?: boolean; isSelfReported?: boolean },
) => putRequest(`/admin/hires/tracking/startups/${startupId}`, params)

export const putAdminHireCandidate = (
  params: AdminHireCandidatePutRequestParams,
) => putRequest('/admin/hire-candidates/one', params)

export const putAdminManualMatchMemo = (
  manualMatchId: number,
  params: { memo: string },
) => putRequest(`/admin/manual-matches/${manualMatchId}`, params)

export const putAdminHireTrackingUserMemo = (
  userId: number,
  params: { hireTrackingMemo: string },
) => putRequest(`/admin/hires/tracking/users/${userId}/save-memo`, params)

export const postDashboardScoutCurationRequestViewLink = (
  curationId: number,
  requestId: number,
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/view-link`,
  )

export const postScoutsRequestsMeRejectInfoFill = (params: {
  value: string
  infoFillRequestId: NumberOrNull
}) => postRequest(`/scouts/requests/me/reject-info-fill`, params)

export const putScoutsRequestsMeIsPublished = (params: { value: boolean }) =>
  putRequest(`/scouts/requests/me/is-published`, params).then(() => {
    params.value &&
      analytics.setIdentify({ profileOpenDate: dayjs().format('YYMMDD') })
  })

export const putAdminHireTrackingChatsStep = (params: {
  proposalId?: number
  applicationId?: number
  chatStep: DashboardChatStatusId
}) => putRequest('/admin/hires/tracking/chats/step', params)

export const postDashboardScoutsProposalBrokingScoutProposal = (
  proposalId: number,
  params: { perStartupCurationScoutRequestId: number; comment: string },
) =>
  postRequest(
    `/dashboard/scouts/proposals/${proposalId}/broking-scout-proposal`,
    params,
  )

export const postScoutsProposalAcceptBrokingScoutProposal = (
  proposalId: number,
  params: { messageId: number; perStartupCurationScoutRequestId: number },
) =>
  postRequest(
    `/scouts/proposals/${proposalId}/accept-broking-scout-proposal`,
    params,
  )

export const postScoutsProposalRejectBrokingScoutProposal = (
  proposalId: number,
  params: {
    messageId: number
    perStartupCurationScoutRequestId: number
    rejectReason: string | null
    reasonIds: number[]
  },
) =>
  postRequest(
    `/scouts/proposals/${proposalId}/reject-broking-scout-proposal`,
    params,
  )

export const postDashboardScoutsProposalAcceptBrokingScoutProposal = (
  proposalId: number,
  params: { perStartupCurationScoutRequestId: number },
) =>
  postRequest(
    `/dashboard/scouts/proposals/${proposalId}/accept-broking-scout-proposal`,
    params,
  )

export const postDashboardScoutsProposalRejectBrokingScoutProposal = (
  proposalId: number,
  params: { perStartupCurationScoutRequestId: number },
) =>
  postRequest(
    `/dashboard/scouts/proposals/${proposalId}/reject-broking-scout-proposal`,
    params,
  )

export const getDashboardPositionToText = (positionId: number) =>
  getRequest(`/dashboard/positions/${positionId}/text`).then(
    ({ data }) => data as { value: string },
  )

export const postDashboardScoutTextToSearches = (
  text: string,
  params: DashboardScoutRequestOneQueryParams,
) =>
  postRequest<{
    explain: string
    search: DashboardScoutRequestSearchFilterParams
  }>(`/dashboard/scouts/text-to-searches`, {
    text,
    search: params,
  })

export const postDashboardScoutSearch = (
  params: DashboardScoutRequestOneQueryParams | { isFromScoutWizard: boolean },
) =>
  postRequest<{
    searchId: number
  }>('/dashboard/scouts/searches', params)

export const postDashboardScoutSearchFeedback = (params: {
  feedback: string
  search: DashboardScoutRequestOneQueryParams
}) => postRequest('/dashboard/scouts/search/feedback', params)

export const postDashboardScoutSearchRequestTalentPool = (params: {
  talentPoolRequest: string
  search: DashboardScoutRequestOneQueryParams
}) => postRequest('/dashboard/scouts/search/request-talent-pool', params)

export const postScoutsRequestsMeRequestInfoFillDraftsClose = (
  category: ScoutProfileDraftType,
) =>
  postRequest('/scouts/requests/me/drafts/close', {
    category,
  })

export const postAdminFileAnonymized = (
  fileId: number,
  fileSystemVersion: FileSystemVersion,
  formData: FormData,
) =>
  postFormRequest(
    `/admin/files/${fileId}/anonymized?fileSystemVersion=${fileSystemVersion}`,
    formData,
  )

export const deleteAdminFileAnonymized = (
  fileId: number,
  fileSystemVersion: FileSystemVersion,
) =>
  deleteRequest(
    `/admin/files/${fileId}/delete-file?fileSystemVersion=${fileSystemVersion}`,
  )

export const putAdminFileIsNotForAnonymization = (
  fileId: number,
  fileSystemVersion: FileSystemVersion,
  value: boolean,
) =>
  putRequest(
    `/admin/files/${fileId}/is-not-for-anonymization?fileSystemVersion=${fileSystemVersion}`,
    { value },
  )

export const putAdminFileReview = (
  fileId: number,
  fileSystemVersion: FileSystemVersion,
  value: boolean,
) =>
  putRequest(
    `/admin/files/${fileId}/review?fileSystemVersion=${fileSystemVersion}`,
    { value },
  )

export const putStartupPositionApplicationTemp = (
  positionId: number,
  shouldDetect: boolean,
  params: ApplicationParams,
) =>
  putRequest<PrivateInfoDetectedResponse>(
    `/startup-positions/${positionId}/applications/temp${
      shouldDetect ? '?shouldDetect=true' : ''
    }`,
    params,
  )

export const putAccountsMe = (params: {
  name?: string
  birthYear?: string | null
  isBirthYearOpen?: boolean
  image?: string | File
  email?: string
  phone?: string
  experience?: number
  interestedPositionTypes?: number[]
  interestedTechStacks?: number[]
}) => {
  const formData = new FormData()
  params.name && formData.append('name', params.name.trim())
  params.birthYear && formData.append('birthYear', params.birthYear.trim())
  params.isBirthYearOpen &&
    formData.append('isBirthYearOpen', params.isBirthYearOpen.toString())
  params.image && formData.append('image', params.image)
  params.email && formData.append('email', params.email.trim())
  params.phone && formData.append('phone', params.phone.trim())
  params.experience !== undefined && // 값이 0일 때도 저장될 수 있어야 함
    formData.append('experience', params.experience.toString())
  params.interestedPositionTypes &&
    params.interestedPositionTypes.length > 0 &&
    params.interestedPositionTypes.forEach((positionType) => {
      formData.append('interestedPositionTypes[]', positionType.toString())
    })
  params.interestedTechStacks &&
    (params.interestedTechStacks.length > 0
      ? params.interestedTechStacks.forEach((techStack) => {
          formData.append('interestedTechStacks[]', techStack.toString())
        })
      : formData.append('interestedTechStacks[]', 'null'))

  return putFormRequest('/accounts/me', formData)
}

export const postStartupPositionApplicationTemp = (positionId: number) =>
  postRequest(`/startup-positions/${positionId}/applications/temp`)

export const postStartupPositionApplicationTempFulfillments = (
  positionId: number,
) =>
  postRequest(`/startup-positions/${positionId}/applications/temp/fulfillments`)

export const postDashboardScoutCuratedRequestAccept = (
  curationId: number,
  requestId: number,
  params: { workStartDate: string | null },
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/accept`,
    params,
  )

export const postDashboardScoutCurationRequestView = (
  curationId: number,
  params: { requestIds: number[] },
) =>
  postRequest(`/dashboard/scouts/curations/${curationId}/requests/view`, params)

export const postDashboardScoutCurationHide = (
  curationId: number,
  params: { value: boolean },
) => postRequest(`/dashboard/scouts/curations/${curationId}/hide`, params)

export const postDashboardScoutCurationRequestBreakDown = (
  curationId: number,
  requestId: number,
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/requests/${requestId}/break-down`,
  )

export const putAdminBrokingRequestStatus = (
  brokingRequestId: number,
  params: { isBreakDown?: boolean; isRestoration?: boolean },
) => putRequest(`/admin/broking-requests/${brokingRequestId}/status`, params)

// isConfirmed가 true면 optional params 필요
export const postAdminBrokingRequestMeet = (
  brokingRequestId: number,
  params?: { isConfirmed: boolean } & Partial<AdminBrokingRequestMeetingInfo>,
) => postRequest(`/admin/broking-requests/${brokingRequestId}/meet`, params)

export const putAdminBrokingRequestMeet = (
  brokingRequestId: number,
  params: Partial<AdminBrokingRequestMeetingInfo>,
) => putRequest(`/admin/broking-requests/${brokingRequestId}/meet`, params)

export const putAdminPerStartupCuration = (
  curationId: number,
  params: AdminPerStartupCurationUpdateParams,
) => putRequest(`/admin/per-startup-curations/${curationId}`, params)

export const deleteAdminPerStartupCuration = (curationId: number) =>
  deleteRequest(`/admin/per-startup-curations/${curationId}`)

export const putAdminStartupSetRemarks = (
  startupId: number,
  params: { remarks: string },
) => putRequest(`/admin/startups/${startupId}/set-remarks`, params)

export const putAdminStartupBanReason = (
  startupId: number,
  params: {
    value: string | null // 이용정지 해제시 null
  },
) => putRequest(`/admin/startups/${startupId}/ban-reason`, params)

export const putAdminBrokingRequestPin = (
  brokingRequestId: number,
  params: { value: boolean },
) => putRequest(`/admin/broking-requests/${brokingRequestId}/pin`, params)

export const postAdminBrokingRequestAlert = (
  brokingRequestId: number,
  params: { isRemind?: boolean; isTracking?: boolean },
) => postRequest(`/admin/broking-requests/${brokingRequestId}/alert`, params)

export const postAdminBrokingRequestAccept = (brokingRequestId: number) =>
  postRequest(`/admin/broking-requests/${brokingRequestId}/accept`)

export const putAdminBrokingRequestRemindMemo = (
  brokingRequestId: number,
  params: {
    remindMemo: string
  },
) =>
  putRequest(`/admin/broking-requests/${brokingRequestId}/remind-memo`, params)

export const putAdminBrokingRequestManager = (
  brokingRequestId: number,
  params: {
    managerId: number | null
  },
) => putRequest(`/admin/broking-requests/${brokingRequestId}/manager`, params)

export const postStartupPositionCurationView = (curationId: number) =>
  postRequest(`/startup-positions/curations/${curationId}/view`)

export const postScoutsSearchesView = (searchId: number) =>
  postRequest(`/dashboard/scouts/searches/${searchId}/view`)

export const putAdminScoutRequest = (
  scoutRequestId: number,
  params: {
    assessMemo?: string
    managerComment?: string
    isCommonCurated?: boolean
    isActivelySeeker?: boolean
  },
) => putRequest(`/admin/scout-requests/${scoutRequestId}`, params)

export const postStartupPositionsCurationFeedback = (params: {
  status: StartupPositionCurationFeedbackType
  reasons?: number[]
  comment?: string
  customReason?: string
}) => postRequest(`/startup-positions/curations/feedback`, params)

export const putStartupPositionsCurationFeedback = (params: {
  status?: StartupPositionCurationFeedbackType
  reasons?: number[]
  comment?: string
  customReason?: string
}) => putRequest(`/startup-positions/curations/feedback`, params)

export const putAdminPreInterview = (
  preInterviewId: number,
  params: {
    summary?: string | null
    videoLink?: string | null
    reportLink?: string | null
    failReason?: string | null
  },
) => putRequest(`/admin/pre-interviews/${preInterviewId}`, params)

export const putAdminStartupSignPreInterviewContract = (
  startupId: number,
  params: { value: boolean },
) =>
  putRequest(`/admin/startups/${startupId}/sign-pre-interview-contract`, params)

export const postAdminPreInterviewResolve = (preInterviewId: number) =>
  postRequest(`/admin/pre-interviews/${preInterviewId}/resolve`)

export const postAdminPreInterviewFail = (preInterviewId: number) =>
  postRequest(`/admin/pre-interviews/${preInterviewId}/fail`)

export const putStartupPositionInterests = (
  positionId: number,
  params: { status: InterestType },
) => putRequest(`/startup-positions/${positionId}/interests`, params)

export const deleteStartupPositionInterests = (positionId: number) =>
  deleteRequest(`/startup-positions/${positionId}/interests`)

export const postStartupPositionApplicationGenerate = (positionId: number) =>
  postRequest(`/startup-positions/${positionId}/applications/generate`)

export const getStartupPositionApplicationGenerate = (positionId: number) =>
  getRequest(`/startup-positions/${positionId}/applications/generate`)

export const getDashboardScoutRequestsProposalAutoPosition = (
  requestId: number,
): Promise<Envelope<DashboardAutoPosition>> =>
  getRequest(`/dashboard/scouts/requests/${requestId}/proposal/auto-position`)

export const getDashboardScoutRequestsProposalAutoFulfillments = (
  requestId: number,
  params: {
    positionId?: number[]
  },
): Promise<Envelope<ScoutFulfillment[]>> =>
  getRequest(
    `/dashboard/scouts/requests/${requestId}/proposal/auto-fulfillments?${qs.stringify(
      params,
    )}`,
  )

export const postDashboardPositionAdvertisements = (
  positionId: number,
  params: DashboardPositionAdvertisementsParams[],
) => postRequest(`/dashboard/positions/${positionId}/advertisements`, params)

export const putAdminPerStartupCurationScoutRequestDetail = async (
  curationId: number,
  requestId: number,
  params: {
    commentForStartup?: string
    memoForManager?: string
    jobHelperAssessMemo?: string
    isBrokingRequestDisabled?: boolean
  },
) =>
  await putRequest(
    `/admin/per-startup-curations/${curationId}/scout-requests/${requestId}`,
    params,
  )

export const deleteAdminPerStartupCurationScoutRequest = async (
  curationId: number,
  requestId: number,
) =>
  await deleteRequest(
    `/admin/per-startup-curations/${curationId}/scout-requests/${requestId}`,
  )

export const putAdminPerStartupCurationScoutRequest = async (
  curationId: number,
  params: {
    scoutRequestIds: number[]
    isPublish: boolean
  },
) =>
  await putRequest(
    `/admin/per-startup-curations/${curationId}/scout-requests`,
    params,
  )

export const putRequestAdminAdsActivelyHireStartups = (params: FormData) =>
  putFormRequest('/admin/ads/actively-hire-startups', params)

export const postDashboardPositionAdvertisementsEvent = async (
  positionId: number,
  params: {
    type: 'advertiseStartup' | 'activelyHire'
  },
) =>
  await postRequest(
    `/dashboard/positions/${positionId}/advertisements/events`,
    params,
  )

// NOTE(seulgi): 스카우트 마법사용이라 사용하지 않고 있고, 임시로 용도에 맞지 않는 GET api 사용했으나, 추후 다시 사용한다면 limit도 보내도록 수정 필요
export const getDashboardScoutRequestsOne = (
  params: DashboardScoutRequestOneQueryParams, // TODO: limit 보내야 함
): Promise<Envelope<BoostingWithItem<DashboardScoutRequestDetail | null>>> =>
  getRequest(`/dashboard/scouts/requests/one?${qs.stringify(params)}`)

export const getDashboardScoutSearchCandidates = (
  searchId: number,
): Promise<Envelope<ScoutSearchCandidate[]>> =>
  getRequest(`/dashboard/scouts/searches/${searchId}/candidates`)

export const postDashboardScoutSearchCandidate = (
  searchId: number,
  params: { scoutRequestId: number },
) => postRequest(`/dashboard/scouts/searches/${searchId}/candidates`, params)

export const deleteDashboardScoutSearchCandidate = (
  searchId: number,
  requestId: number,
) =>
  deleteRequest(
    `/dashboard/scouts/searches/${searchId}/candidates/${requestId}`,
  )

export const putDashboardScoutSearch = (
  searchId: number,
  params: { name?: string; isScoutWizardTerminated?: boolean },
) => putRequest(`/dashboard/scouts/searches/${searchId}`, params)

export const putAccountsMeAlertSetting = (
  params: Partial<AccountAlertSetting>,
) => putRequest('/accounts/me/alert-settings', params)

export const postExternalPartnerZighangEvents = (params: {
  userId: number | null
  clientId: string // 직행 유저식별자
  targetUrl: string | null // 어디로 유입 됐는지
  zighangSourceUrl: string | null // 직행 내 어디서 유입 됐는지
}) => postRequest('/external-partners/zighang/events', params)

export const postScoutProposalSelectAnswer = (
  proposalId: number,
  params: {
    messageId: number
    selectedAnswer: ScoutProposalSelectAnswerEnum
    perStartupCurationScoutRequestId: number
    message?: string
  },
) => postRequest(`/scouts/proposals/${proposalId}/select-answer`, params)

export const postScoutRequestMeAnalyzeFeedback = (params: {
  isHelpful?: boolean
  reasons?: string[]
  isReanalysisRequested?: boolean
}) => postRequest(`/scouts/requests/me/analyze/feedback`, params)

export const postStartupPositionAnalyze = (
  positionId: number,
  params?: { fileIds: number[] },
) => postRequest(`/startup-positions/${positionId}/analyze`, params)

export const postStartupPositionAnalysisView = (positionId: number) =>
  postRequest(`/startup-positions/${positionId}/analysis/view`)

export const getStartupPositionAnalysis = (positionId: number) =>
  getRequest(`/startup-positions/${positionId}/analysis`).then(
    (res) => res as Envelope<StartupPositionAnalysis>,
  )

export const postGuestTokenCookie = () => postRequest(`/guest/token-cookie`)

export const postDashboardChatsStep = () => postRequest(`/dashboard/chats/step`)

export const postDashboardScoutCurationRequestReRecommend = (
  curationId: number,
  params: { requestMessage: string },
) =>
  postRequest(
    `/dashboard/scouts/curations/${curationId}/request-recommend`,
    params,
  )
